/**
 * Admin Shell — layout for all admin pages.
 * Sidebar nav + main content area.
 */
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useTranslation } from "../../i18n/I18nContext";
import { useAuth } from "../../contexts/AuthContext";
import { useLocalePath } from "../../hooks/useLocalePath";

const NAV_ITEMS = [
  { label: "Dashboard",      icon: "mdi:view-dashboard-outline",    path: "/admin/dashboard" },
  { label: "Google Import",  icon: "mdi:cloud-download-outline",    path: "/admin/import" },
  { label: "Categories",     icon: "mdi:tag-multiple-outline",      path: "/admin/categories" },
  { label: "Businesses",     icon: "mdi:office-building-outline",   path: "/admin/businesses" },
  { label: "Biz Analytics",  icon: "mdi:chart-areaspline",          path: "/admin/business-analytics" },
  { label: "Reviews",        icon: "mdi:star-outline",               path: "/admin/reviews" },
  { label: "Users",          icon: "mdi:account-group-outline",     path: "/admin/users" },
  { label: "Mailer",         icon: "mdi:email-outline",              path: "/admin/mailer", i18nKey: "admin.mailer.nav" },
  { label: "Developer",     icon: "mdi:code-braces",               path: "/admin/developer" },
  { label: "Audit Logs",    icon: "mdi:shield-search",             path: "/admin/audit" },
  { label: "Notifications", icon: "mdi:bell-badge-outline",        path: "/admin/notifications" },
  { label: "Subscriptions", icon: "mdi:credit-card-check-outline", path: "/admin/subscriptions" },
  { label: "SEO Audit",     icon: "mdi:magnify-scan",              path: "/admin/seo" },
  { label: "Settings",      icon: "mdi:cog-outline",               path: "/admin/settings" },
  { label: "Blog CMS", icon: "mdi:post-outline", path: "/admin/blog", children: [
    { label: "Dashboard", path: "/admin/blog/dashboard" },
    { label: "Posts", path: "/admin/blog" },
    { label: "Categories", path: "/admin/blog/categories" },
    { label: "Tags", path: "/admin/blog/tags" },
    { label: "Authors", path: "/admin/blog/authors" },
    { label: "Redirects", path: "/admin/blog/redirects" },
    { label: "Analytics", path: "/admin/blog/analytics" },
  ]},
];

const AdminShell = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const lp = useLocalePath();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate(lp("/login"));
  };

  const isActive = (path) => {
    const full = lp(path);
    if (pathname === full || pathname.startsWith(full + "/")) return true;
    // Import hub + nested Google Import flows share one nav item
    if (path === "/admin/import") {
      const gi = lp("/admin/google-import");
      return pathname === gi || pathname.startsWith(gi + "/");
    }
    if (path === "/admin/blog") {
      const b = lp("/admin/blog");
      return pathname === b || pathname.startsWith(b + "/");
    }
    return false;
  };

  return (
    <div className="bg-[#F4F5FA] min-h-screen flex flex-row items-stretch">

      {/* ── Sidebar ── */}
      <aside className="w-[240px] bg-white border-r border-[#D4D6FF] flex flex-col flex-shrink-0 min-h-screen">

        {/* Logo */}
        <div className="px-5 pt-5 pb-4 flex-shrink-0">
          <Link to={lp("/admin/dashboard")}>
            <img src="/icons/logo_horizontal.webp" alt="logo" className="w-[130px] h-[28px] object-cover" />
          </Link>
          <span className="mt-2 inline-block text-[10px] font-semibold uppercase tracking-widest text-[#9CA3AF] bg-[#F4F5FA] px-2 py-0.5 rounded">
            Admin Panel
          </span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 pb-4 flex flex-col gap-0.5 overflow-y-auto">
          {NAV_ITEMS.map((item) => {
            const active = isActive(item.path);
            const hasChildren = item.children?.length > 0;
            return (
              <div key={item.path}>
                <Link
                  to={lp(hasChildren ? item.children[0].path : item.path)}
                  className={`flex flex-row items-center gap-3 px-3 py-2.5 rounded-[8px] text-sm font-medium transition-all duration-200 ${
                    active
                      ? "bg-[linear-gradient(90deg,#1B51EC_-10%,#1B51EC_104%)] text-white"
                      : "text-[#4A5565] hover:text-primary hover:bg-[#F4F5FA]"
                  }`}
                >
                  <Icon icon={item.icon} className="w-4 h-4 flex-shrink-0" />
                  <span>{item.i18nKey ? t(item.i18nKey) : item.label}</span>
                </Link>
                {hasChildren && active && (
                  <div className="ml-7 mt-0.5 flex flex-col gap-0.5">
                    {item.children.map((child) => {
                      const childActive = pathname === lp(child.path);
                      return (
                        <Link
                          key={child.path}
                          to={lp(child.path)}
                          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                            childActive
                              ? "text-blue-600 bg-blue-50"
                              : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {child.label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* User footer */}
        <div className="p-3 border-t border-[#E5E7EB] flex-shrink-0 flex flex-col gap-1">
          <div className="flex flex-row items-center gap-3 px-2 py-2">
            <div className="w-8 h-8 rounded-full bg-primary/10 border border-primary flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-semibold text-primary">
                {(user?.firstName?.[0] || user?.name?.[0] || "A").toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-dark-blue truncate">
                {user?.firstName || user?.name || "Admin"}
              </p>
              <p className="text-[10px] text-[#6A7282] truncate">{user?.email || ""}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex flex-row items-center gap-2 px-3 py-2 rounded-[8px] text-sm font-medium text-red-500 hover:bg-red-50 transition-colors"
          >
            <Icon icon="mdi:logout" className="w-4 h-4 flex-shrink-0" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <main className="flex-1 p-6 flex flex-col gap-6 min-w-0 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminShell;
